import { GoogleAnalytics } from '@next/third-parties/google';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { emitPageViewGAEvent, emitPageViewFBEvent, emitGAEventSetUserId, FB_PIXEL_ID } from '../../../utils/ga-event-helpers';
import { installConversionLinker } from '../../../utils/conversionLinker';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { consentRequiredRegions } from '../../modules/Constants';

// Derives the analytics vertical from the real path prefix (TPD-573 A10) so
// SRP/city/slug/results pages — not just landings — emit {vertical}_page_view.
const deriveVerticalFromPath = (path: string): string | null => {
	const p = path.split('?')[0].split('#')[0];
	if (p === '/') return 'flights';
	if (p === '/hotels' || p.startsWith('/hotels/')) return 'hotels';
	if (p === '/flights' || p.startsWith('/flights/')) return 'flights';
	if (p === '/cars' || p.startsWith('/cars/')) return 'cars';
	if (p === '/cruises' || p.startsWith('/cruises/')) return 'cruises';
	return null;
};

let consentBridgeInstalled = false;
// Bridges the Cookiebot CMP accept/decline to gtag('consent','update') (TPD-573 A13).
const wireConsentModeUpdateBridge = () => {
	if (typeof window === 'undefined' || consentBridgeInstalled) return;
	consentBridgeInstalled = true;
	const w = window as any;
	const applyConsent = () => {
		const c = w.Cookiebot?.consent;
		if (!c || typeof w.gtag !== 'function') return;
		w.gtag('consent', 'update', {
			ad_storage: c.marketing ? 'granted' : 'denied',
			ad_user_data: c.marketing ? 'granted' : 'denied',
			ad_personalization: c.marketing ? 'granted' : 'denied',
			analytics_storage: c.statistics ? 'granted' : 'denied',
			functionality_storage: c.preferences ? 'granted' : 'denied',
			personalization_storage: c.preferences ? 'granted' : 'denied',
		});
	};
	window.addEventListener('CookiebotOnConsentReady', applyConsent);
	window.addEventListener('CookiebotOnAccept', applyConsent);
	window.addEventListener('CookiebotOnDecline', applyConsent);
};

const FBPixelScript: FC<{ pixelId: string; externalId?: string }> = ({ pixelId, externalId }) => {
	if (!pixelId) return null;
	return (
		<>
			<Script id="fb-pixel" strategy="afterInteractive">
				{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}', ${externalId ? `{ external_id: '${externalId}' }` : '{}'});
        `}
			</Script>
			<noscript>
				<img
					height="1"
					width="1"
					style={{ display: 'none' }}
					src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1${externalId ? `&external_id=${externalId}` : ''}`}
					alt=""
				/>
			</noscript>
		</>
	);
};

// TikTok pixel base + page view — parity with the removed GTM tag 105. Cookiebot
// (data-blockingmode="auto") consent-gates the script, matching the GTM behaviour.
const TikTokPixelScript: FC<{ pixelId: string }> = ({ pixelId }) => {
	if (!pixelId) return null;
	return (
		<Script id="tiktok-pixel" strategy="afterInteractive">
			{`
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=d.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=d.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
          ttq.load('${pixelId}');
          ttq.page();
        }(window, document, 'ttq');
      `}
		</Script>
	);
};

// Bing UET base — parity with the removed GTM tag 17. Hard-load pageLoad only
// (no SPA auto-tracking), matching GTM. Cookiebot consent-gates the script.
const BingUetScript: FC<{ tagId: string }> = ({ tagId }) => {
	if (!tagId) return null;
	return (
		<Script id="bing-uet" strategy="afterInteractive">
			{`
        (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"${tagId}"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&"loaded"!==s&&"complete"!==s||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
      `}
		</Script>
	);
};

// Klaviyo onsite — parity with the removed GTM tag 219.
const KlaviyoScript: FC<{ companyId: string }> = ({ companyId }) => {
	if (!companyId) return null;
	return (
		<Script
			id="klaviyo-onsite"
			strategy="afterInteractive"
			src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${companyId}`}
		/>
	);
};

// Twitter/X pixel base + auto page view — parity with the removed GTM tag 210
// (fired on cookie_consent_update). Per-vertical search events fire from
// emitSearchTrackingEvents via emitTwitterEvent. Cookiebot consent-gates it.
const TwitterPixelScript: FC<{ pixelId: string }> = ({ pixelId }) => {
	if (!pixelId) return null;
	return (
		<Script id="twitter-pixel" strategy="afterInteractive">
			{`
        !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
        twq('config','${pixelId}');
      `}
		</Script>
	);
};

const RenderGA: FC<{ isSiteLoaded: boolean; enabledVert?: string; uid?: string; imid?: string }> = ({
	isSiteLoaded,
	uid,
	imid,
}) => {
	const router = useRouter();
	const lastGaPath = useRef<string | null>(null);
	const lastFbPath = useRef<string | null>(null);
	// Fallback so a slow/failed init() never DROPS the page_view: once this elapses we
	// fire {vertical}_page_view even without a user_id (TPD-573 A10).
	const [userIdWaitElapsed, setUserIdWaitElapsed] = useState(false);

	// useLayoutEffect (not useEffect) so the Consent Mode default + Conversion
	// Linker land in dataLayer BEFORE <GoogleAnalytics>'s afterInteractive
	// gtag('config') runs: next/script pushes config from a PASSIVE useEffect, and
	// all layout effects run before all passive effects across the tree — so this
	// guarantees consent precedes config even though GoogleAnalytics is now mounted
	// eagerly (TPD-573 A10 follow-up). Component is client-only (rendered after
	// _app's `hydrated` flips), so there is no SSR useLayoutEffect warning.
	useLayoutEffect(() => {
		if (typeof window !== 'undefined') {
			window.dataLayer = window.dataLayer || [];
			window.gtag =
				window.gtag ||
				function () {
					// eslint-disable-next-line prefer-rest-params
					(window.dataLayer = window.dataLayer || []).push(arguments);
				};

			// Code-owned Consent Mode default (TPD-573 A11/A12), scoped ONLY to regions
			// where consent is legally required (EEA/UK/CH): deny storage there until the
			// Cookiebot CMP grants it; wait_for_update lets the first hit resolve to the
			// real consent state before sending. Regions NOT listed have no default, so
			// gtag's built-in 'granted' applies and US/CA/AU/IL/… are attributed at once.
			//
			// A region-agnostic `gtag('consent','default', denied)` used to follow this
			// (denied storage for ALL regions) but is REMOVED (TPD-573 A10/A13 funnel
			// re-audit): it had no wait_for_update, so the early router.isReady
			// {vertical}_page_view in non-EEA regions fired cookieless (no client_id →
			// user_pseudo_id NULL), orphaning ~90% of non-EEA page_views from the
			// consented search session and deflating the view->search funnel. Consent is
			// not legally required outside the EEA, so granted-by-default is correct there
			// and restores the pre-Consent-Mode attribution. The Cookiebot bridge below
			// still downgrades to denied if a user explicitly declines.
			const deniedDefault = {
				functionality_storage: 'denied',
				security_storage: 'granted',
				ad_personalization: 'denied',
				ad_storage: 'denied',
				ad_user_data: 'denied',
				analytics_storage: 'denied',
				personalization_storage: 'denied',
			};
			window.gtag('consent', 'default', {
				...deniedDefault,
				region: consentRequiredRegions,
				wait_for_update: 500,
			});

			// false = attribution-preserving (matches the value GTM tag 227 enforced, B8).
			window.gtag('set', 'ads_data_redaction', false);
			window.gtag('set', 'url_passthrough', false);

			// Cookiebot accept/decline -> gtag consent update (A13).
			wireConsentModeUpdateBridge();
			// Code-native Google Ads Conversion Linker so gclid _gcl_* survives GTM removal.
			installConversionLinker();
		}
	}, []);

	useEffect(() => {
		// Give init() a short window to publish the current-session uid/imid before we
		// fire the page_view without a user_id — never drop the event (TPD-573 A10).
		const t = setTimeout(() => setUserIdWaitElapsed(true), 2000);
		return () => clearTimeout(t);
	}, []);

	useEffect(() => {
		// Fire {vertical}_page_view on route change (TPD-573 A10): prefix-derive the
		// vertical from the real path (covers SRP/city/slug/results), de-dupe on path.
		// Delivery relies on <GoogleAnalytics> being mounted eagerly (config +
		// @next/third-parties currDataLayerName ready before this passive effect).
		//
		// user_id (TPD-573 follow-up, mirrors besthotelsprices): uid+imid are
		// SESSION-scoped and rotate (BQ: ~99% of returning users get a NEW uid), so we
		// attach the CURRENT session's id — NOT a stale persisted value. init() publishes
		// uid/imid to Redux (these props, not redux-persisted → always current session),
		// so we wait for them: the page_view then carries the same user_id the later
		// search will (user_id / COALESCE funnels join; GA4 can dedupe step1 by user_id).
		// If init() is slow/fails, the userIdWaitElapsed fallback fires the page_view
		// WITHOUT user_id rather than dropping it.
		if (!router.isReady) return;
		const hasUserId = Boolean(uid && imid);
		if (!hasUserId && !userIdWaitElapsed) return;

		const path = router.asPath.split('?')[0].split('#')[0];
		if (path === lastGaPath.current) return;
		lastGaPath.current = path;

		const vertical = deriveVerticalFromPath(path);
		if (vertical) {
			if (hasUserId) emitGAEventSetUserId(`${uid}${imid}`);
			emitPageViewGAEvent(vertical);
		}
	}, [router.asPath, router.isReady, uid, imid, userIdWaitElapsed]);

	useEffect(() => {
		// FB PageView stays gated on isSiteLoaded: the base pixel only runs fbq('init'),
		// so the PageView must wait until fbq has loaded (emitPageViewFBEvent no-ops
		// otherwise). Generic base-pixel signal — fire on EVERY page (home, blog,
		// compare, SRP), not only vertical pages (TPD-573 re-audit).
		if (!isSiteLoaded || !router.isReady) return;

		const path = router.asPath.split('?')[0].split('#')[0];
		if (path === lastFbPath.current) return;
		lastFbPath.current = path;

		emitPageViewFBEvent();
	}, [router.asPath, isSiteLoaded, router.isReady]);

	const fbPixelId = FB_PIXEL_ID;
	const tiktokPixelId = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID || 'C9T1UU3C77U4P6750IRG';
	const bingUetId = process.env.NEXT_PUBLIC_BING_UET_ID || '17511197';
	const klaviyoCompanyId = process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID || 'SegmB8';
	const twitterPixelId = process.env.NEXT_PUBLIC_TWITTER_PIXEL_ID || 'oh4k9';
	const externalId = uid && imid ? `${uid}${imid}` : undefined;

	return (
		<>
			{/* gtag.js loads early (afterInteractive), NOT gated on isSiteLoaded/window 'load'
			    (TPD-573 A10 firing-fix follow-up). gtag('config', …) must run BEFORE the
			    router.isReady {vertical}_page_view is queued into dataLayer — otherwise the
			    early page_view sits in the queue pre-config and gtag drops it, so it never
			    reaches the search session. That deflated view->search session-coverage to
			    ~40% even after the trigger moved to router.isReady: moving the page_view
			    earlier doesn't help while the GA initializer itself stays behind window-load.
			    @next/third-parties already defers this to afterInteractive, so the old
			    isSiteLoaded gate was redundant for render-blocking and harmful for attribution. */}
			<GoogleAnalytics gaId="G-RRHFY1EM93" />
			{/* Marketing pixels + AdSense stay deferred until window 'load' to protect LCP. */}
			{isSiteLoaded && (
				<>
					<FBPixelScript pixelId={fbPixelId} externalId={externalId} />
					<TikTokPixelScript pixelId={tiktokPixelId} />
					<BingUetScript tagId={bingUetId} />
					<KlaviyoScript companyId={klaviyoCompanyId} />
					<TwitterPixelScript pixelId={twitterPixelId} />
					<Script
						id="googleads"
						data-ad-client="ca-pub-2732183997877262"
						async
						src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"
					/>
				</>
			)}
		</>
	);
};

export default RenderGA;
